import React, { useState } from 'react';
import { Box, Container, Typography, Paper, TextField, Button, Snackbar, FormControl, FormLabel, FormHelperText, RadioGroup, FormControlLabel, Radio, Select, MenuItem } from '@mui/material';
import { getDefaultAdvisorName, useAvatar } from '../contexts/AvatarContext';
import { useLanguage } from '../contexts/LanguageContext';

const AVATAR_STYLES = [
  ['adventurer', '探險者', 'Adventurer'],
  ['adventurer-neutral', '中立探險者', 'Adventurer Neutral'],
  ['avataaars', 'Avataaars', 'Avataaars'],
  ['avataaars-neutral', '中立Avataaars', 'Avataaars Neutral'],
  ['big-ears', '大耳朵', 'Big Ears'],
  ['big-ears-neutral', '中立大耳朵', 'Big Ears Neutral'],
  ['big-smile', '大笑容', 'Big Smile'],
  ['bottts', 'Bottts', 'Bottts'],
  ['bottts-neutral', '中立Bottts', 'Bottts Neutral'],
  ['croodles', 'Croodles', 'Croodles'],
  ['croodles-neutral', '中立Croodles', 'Croodles Neutral'],
  ['fun-emoji', '有趣emoji', 'Fun Emoji'],
  ['icons', '圖示', 'Icons'],
  ['identicon', 'Identicon', 'Identicon'],
  ['initials', '首字母', 'Initials'],
  ['lorelei', 'Lorelei', 'Lorelei'],
  ['lorelei-neutral', '中立Lorelei', 'Lorelei Neutral'],
  ['micah', 'Micah', 'Micah'],
  ['miniavs', 'Miniavs', 'Miniavs'],
  ['notionists', 'Notionists', 'Notionists'],
  ['notionists-neutral', '中立Notionists', 'Notionists Neutral'],
  ['open-peeps', '開放人物', 'Open Peeps'],
  ['personas', '人物', 'Personas'],
  ['pixel-art', '像素藝術', 'Pixel Art'],
  ['pixel-art-neutral', '中立像素藝術', 'Pixel Art Neutral'],
  ['rings', '環形', 'Rings'],
  ['shapes', '形狀', 'Shapes'],
  ['thumbs', '拇指', 'Thumbs'],
];

const AVATAR_COLORS = ['b6e3f4', 'ffdfbf', 'ffd1dc', 'c1f0c1', 'd4e7ff', 'ffe5d9', 'f0e6ff', 'fff4e6'];

const Settings = () => {
  const { t, tr, language, setLanguage, languages, isEnglish } = useLanguage();
  const [selectedLanguage, setSelectedLanguage] = useState(language);
  const [userName, setUserName] = useState(() => {
    return localStorage.getItem('advisorName') || getDefaultAdvisorName(language);
  });
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [inputValue, setInputValue] = useState(() => {
    return localStorage.getItem('advisorName') || getDefaultAdvisorName(language);
  });
  const [gender, setGender] = useState(() => {
    return localStorage.getItem('advisorGender') || 'male';
  });
  const [avatarStyle, setAvatarStyle] = useState(() => {
    return localStorage.getItem('avatarStyle') || 'avataaars';
  });
  const [avatarBgColor, setAvatarBgColor] = useState(() => {
    return localStorage.getItem('avatarBgColor') || 'b6e3f4';
  });
  const [avatarSeed, setAvatarSeed] = useState(() => {
    return localStorage.getItem('avatarSeed') || Math.floor(Math.random() * 10000).toString();
  });
  const { updateAvatarSettings } = useAvatar();

  const handleNameChange = (event) => {
    setInputValue(event.target.value);
  };

  const generateRandomSeed = () => {
    const newSeed = Math.floor(Math.random() * 10000).toString();
    setAvatarSeed(newSeed);
  };

  const handleSave = () => {
    // An untouched default name follows the newly selected language; a saved name is kept as is.
    const keepsDefaultName = !localStorage.getItem('advisorName') && inputValue === getDefaultAdvisorName(language);
    const nameToSave = keepsDefaultName ? getDefaultAdvisorName(selectedLanguage) : inputValue;
    setUserName(nameToSave);
    setInputValue(nameToSave);
    localStorage.setItem('advisorName', nameToSave);
    localStorage.setItem('advisorGender', gender);
    localStorage.setItem('avatarStyle', avatarStyle);
    localStorage.setItem('avatarBgColor', avatarBgColor);
    localStorage.setItem('avatarSeed', avatarSeed);
    setLanguage(selectedLanguage);
    updateAvatarSettings({
      style: avatarStyle,
      bgColor: avatarBgColor,
      seed: avatarSeed,
      name: nameToSave,
    });
    setShowSnackbar(true);
  };

  const handleCloseSnackbar = () => {
    setShowSnackbar(false);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4 }}>
        {t('settings.title')}
      </Typography>
      <Paper elevation={0} sx={{ p: 3, bgcolor: '#f5f5f5' }}>
        <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
          <Box sx={{ mb: 2 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend">{t('settings.language')}</FormLabel>
              <RadioGroup
                row
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
              >
                {Object.entries(languages).map(([code, info]) => (
                  <FormControlLabel key={code} value={code} control={<Radio />} label={code === 'zh-TW' ? tr(info.label, 'Traditional Chinese') : info.label} />
                ))}
              </RadioGroup>
              <FormHelperText>{t('settings.languageHint')}</FormHelperText>
            </FormControl>
          </Box>
          <Typography variant="h6" component="h2" gutterBottom sx={{ mt: 4 }}>
            {t('settings.name')}
          </Typography>
          <TextField
            fullWidth
            value={inputValue}
            onChange={handleNameChange}
            placeholder={t('settings.namePlaceholder')}
            variant="outlined"
            sx={{ mt: 1, mb: 2 }}
          />
          <Box sx={{ mt: 4, mb: 2 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend">{t('settings.gender')}</FormLabel>
              <RadioGroup
                row
                value={gender}
                onChange={(e) => setGender(e.target.value)}
              >
                <FormControlLabel value="male" control={<Radio />} label={t('settings.male')} />
                <FormControlLabel value="female" control={<Radio />} label={t('settings.female')} />
              </RadioGroup>
            </FormControl>
          </Box>
          <Box sx={{ mt: 4, mb: 2 }}>
            <FormControl fullWidth>
              <FormLabel component="legend">{t('settings.avatarStyle')}</FormLabel>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                <Select
                  value={avatarStyle}
                  onChange={(e) => setAvatarStyle(e.target.value)}
                  size="small"
                  sx={{ minWidth: 200 }}
                >
                  {AVATAR_STYLES.map(([value, zh, en]) => (
                    <MenuItem key={value} value={value}>{isEnglish ? en : zh}</MenuItem>
                  ))}
                </Select>
                <Button
                  variant="outlined"
                  onClick={generateRandomSeed}
                  size="small"
                >
                  {t('settings.randomize')}
                </Button>
                <Box sx={{ ml: 2 }}>
                  <img
                    src={`https://api.dicebear.com/7.x/${avatarStyle}/svg?seed=${avatarStyle === 'initials' ? userName[0] : avatarSeed}&backgroundColor=${avatarBgColor}`}
                    alt="Avatar preview"
                    style={{ width: 60, height: 60, borderRadius: '50%' }}
                  />
                </Box>
              </Box>
            </FormControl>
          </Box>
          <Box sx={{ mt: 4, mb: 2 }}>
            <FormControl fullWidth>
              <FormLabel component="legend">{t('settings.avatarBg')}</FormLabel>
              <Select
                value={avatarBgColor}
                onChange={(e) => setAvatarBgColor(e.target.value)}
                size="small"
                sx={{ mt: 1 }}
              >
                {AVATAR_COLORS.map((color) => (
                  <MenuItem key={color} value={color}>{t(`settings.colors.${color}`)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Button
            variant="contained"
            onClick={handleSave}
            fullWidth
            sx={{
              bgcolor: '#1976d2',
              '&:hover': {
                bgcolor: '#115293'
              }
            }}
          >
            {t('settings.save')}
          </Button>
          <Snackbar
            open={showSnackbar}
            autoHideDuration={3000}
            onClose={handleCloseSnackbar}
            message={t('settings.saved')}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          />
        </Box>
      </Paper>
    </Container>
  );
};

export default Settings;
